/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/TryParseVersion.ts
 * @create:      2025-07-01 18:01:20.184
 * @modify:      2025-07-11 21:19:12.961
 * @version:     6.0.0
 * @times:       4
 * @lines:       86
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { GetStringBetween } from "../string/GetStringBetween";
import { TrimAll } from "../string/TrimAll";
import { TrimEnd } from "../string/TrimEnd";

/**
 * Attempts to parse a version string from the given line based on specified keys.
 * First, it checks for key-value pairs separated by a colon.
 * If no match is found, it looks for XML-like tags containing the version.
 *
 * @param line - The string from which to parse the version.
 * @param keys - Optional keys to use for parsing. If not provided, "version" is used by default.
 * @returns The parsed version string if found; otherwise, undefined.
 * 
 * @since 1.0.5
 * @version 2023-12-01
 */
export function TryParseVersion(line: string, ...keys: string[]) {
    if (!line) {
        return undefined;
    }

    const keyValue = line.split(":");
    if (keyValue.length === 2) {
        let key = keyValue[0];
        let value = TrimEnd(keyValue[1].trim(), ",");

        key = TrimAll(key, [" ", "'", "\""]);
        value = TrimAll(value, [" ", "'", "\""]);

        if (keys.length === 0) {
            if (key === "version") {
                return value;
            }
        } else {
            if (keys.includes(key)) {
                return value;
            }
        }
    }

    if (keys.length === 0) {
        keys.push("Version");
    }

    for (const key of keys) {
        const value = GetStringBetween(line, `<${key}>`, `</${key}>`)[0];
        if (value) {
            return value.trim();
        }
    }

    return undefined;
}
